import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import NameWithAvatar from "@/components/NameWithAvatar";
import FloorPlans from "@/pages/FloorPlans";
import FloorPlanAlertBanner from "@/components/FloorPlanAlertBanner";
import { Plus, Trash2, Edit2, X, Check, Paperclip, FileText, Image as ImageIcon, RotateCcw, Lock, Download, FolderArchive, ChevronDown, ChevronUp, History, AlertTriangle, CheckCircle2, Clock, CalendarClock, Truck, Crown, Store, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatCurrency, wholeDollarHint } from "@/lib/api";

// USD formatter for audit tables — closing runs in USD (Massachusetts dealer).
const formatUSD = (n) => formatCurrency(Number(n) || 0);

const CATEGORIES = ["rent", "water", "electricity", "internet", "phone", "salary", "marketing", "maintenance", "parts", "taxes", "floor_plan", "post_sale", "fi_commission", "other"];

function Input({ label, value, set, type = "text", testid, required }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        onChange={(e) => set(e.target.value)}
        className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
      />
    </div>
  );
}

function EditablePrice({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? 0));

  const commit = () => {
    setEditing(false);
    if (Number(draft) !== Number(value)) onSave(draft);
  };

  if (editing) {
    return (
      <input
        data-testid={testid}
        type="number"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { setDraft(String(value ?? 0)); setEditing(false); }
        }}
        className="w-32 bg-surface border border-primary focus:outline-none px-2 h-9 text-sm font-display font-bold text-right"
      />
    );
  }
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => { setDraft(String(value ?? 0)); setEditing(true); }}
      className="font-display font-bold hover:text-primary transition-colors border-b border-dashed border-text-secondary/40 hover:border-primary"
      title="Click to edit"
    >
      {formatCurrency(Number(value) || 0)}
    </button>
  );
}

function LeadSourcesPanel({ data, monthLabel, t }) {
  // Empty guard — before any data is loaded or when the month has no sales.
  if (!data || !data.sources || data.sources.length === 0) {
    return (
      <div className="border border-border mb-10 p-6" data-testid="lead-sources-panel">
        <div className="flex items-center justify-between mb-3">
          <p className="label-eyebrow text-primary">
            {t("lead_source_panel_title") || "Origem das vendas"} · {monthLabel}
          </p>
          <p className="text-[11px] text-text-secondary">{t("lead_source_panel_hint") || "De onde chegaram os clientes que fecharam esse mês."}</p>
        </div>
        <p className="text-text-secondary text-sm text-center py-8">
          {t("lead_source_panel_empty") || "Sem vendas com origem informada neste mês."}
        </p>
      </div>
    );
  }

  const total = data.total_count || 1; // avoid /0
  const maxCount = Math.max(...data.sources.map((s) => s.count));

  // Highlight color per source (Tailwind classes are precomputed to survive JIT purge).
  const colorFor = (key) => {
    const map = {
      walk_in: "bg-emerald-500",
      referral: "bg-amber-500",
      bdc: "bg-cyan-500",
      facebook: "bg-blue-600",
      instagram: "bg-pink-500",
      whatsapp: "bg-green-500",
      marketplace: "bg-indigo-500",
      tiktok: "bg-slate-800",
      youtube: "bg-red-600",
      google_ads: "bg-yellow-500",
      cargurus: "bg-teal-500",
      carfax: "bg-blue-400",
      craigslist: "bg-purple-500",
      website: "bg-sky-500",
      phone: "bg-orange-500",
      repeat_customer: "bg-fuchsia-500",
      unknown: "bg-slate-500",
      other: "bg-slate-400",
    };
    return map[key] || "bg-primary";
  };

  const topSource = data.sources[0];
  const topLabel = t(`lead_source_${topSource.source}`) || topSource.source;

  return (
    <div className="border border-border mb-10" data-testid="lead-sources-panel">
      <div className="bg-surface px-4 py-3 border-b border-border flex items-center justify-between">
        <div>
          <p className="label-eyebrow text-primary">
            {t("lead_source_panel_title") || "Origem das vendas"} · {monthLabel}
          </p>
          <p className="text-[11px] text-text-secondary mt-0.5">
            {t("lead_source_panel_hint") || "De onde chegaram os clientes que fecharam esse mês."}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-text-secondary uppercase tracking-wider">
            {t("lead_source_top_channel") || "Canal top"}
          </p>
          <p className="font-display font-bold text-brand text-sm">
            {topLabel} · {topSource.count} {topSource.count === 1 ? "venda" : "vendas"}
          </p>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {data.sources.map((s) => {
          const pct = Math.round((s.count / total) * 100);
          const barW = Math.round((s.count / maxCount) * 100);
          const label = t(`lead_source_${s.source}`) || s.source;
          return (
            <div key={s.source} data-testid={`ls-row-${s.source}`} className="grid grid-cols-[minmax(140px,180px)_1fr_auto] gap-3 items-center">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${colorFor(s.source)}`} />
                <span className="text-sm font-display font-medium truncate">{label}</span>
              </div>
              <div className="h-6 bg-background border border-border overflow-hidden relative">
                <div
                  className={`${colorFor(s.source)} h-full transition-all`}
                  style={{ width: `${barW}%` }}
                />
                <span className="absolute inset-0 flex items-center justify-end pr-2 text-[10px] text-text-secondary">
                  {pct}%
                </span>
              </div>
              <div className="text-right shrink-0">
                <p className="font-display font-bold text-sm">{s.count}</p>
                <p className="text-[10px] text-text-secondary">{formatCurrency(s.revenue)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StoresComparisonPanel({ data, monthLabel }) {
  // Owner-only side-by-side view of every store's month. Rendered ONLY when the
  // backend returned data (endpoint 403s for anyone that isn't the owner).
  const stores = data?.stores || [];
  if (stores.length < 2) return null;
  const clrs = [
    { band: "bg-brand", text: "text-brand" },
    { band: "bg-cyan-500", text: "text-cyan-400" },
    { band: "bg-purple-500", text: "text-purple-400" },
    { band: "bg-amber-500", text: "text-amber-400" },
  ];
  const bestNet = Math.max(...stores.map((s) => s.net_profit));
  const metrics = [
    { key: "sales_count", label: "Vendas no mês", fmt: (v) => v },
    { key: "revenue", label: "Receita", fmt: formatCurrency },
    { key: "gross_profit", label: "Lucro bruto", fmt: formatCurrency },
    { key: "avg_profit_per_car", label: "Lucro médio por carro", fmt: formatCurrency },
    { key: "op_expenses", label: "Despesas operacionais", fmt: (v) => (v > 0 ? `−${formatCurrency(v)}` : formatCurrency(0)), warn: true },
    { key: "paid_commissions", label: "Comissões pagas", fmt: (v) => (v > 0 ? `−${formatCurrency(v)}` : formatCurrency(0)), warn: true },
    { key: "receivables", label: "Recebíveis no mês", fmt: formatCurrency },
    { key: "net_profit", label: "Lucro líquido", fmt: formatCurrency, hero: true },
    { key: "in_stock", label: "Em estoque", fmt: (v) => v },
    { key: "invested", label: "Investido no pátio", fmt: formatCurrency },
  ];
  return (
    <div className="border border-border mb-10" data-testid="stores-comparison">
      <div className="bg-surface px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <p className="label-eyebrow text-primary inline-flex items-center gap-2">
          <Store size={14} /> Comparativo de Lojas · {monthLabel}
        </p>
        <p className="text-[10px] uppercase tracking-widest text-text-secondary inline-flex items-center gap-1">
          <Lock size={10} /> Visível só pro dono · fechamentos continuam separados
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="p-3 text-left text-[10px] uppercase tracking-widest text-text-secondary font-normal w-52"></th>
              {stores.map((s, i) => (
                <th key={s.dealership_id} className="p-0 text-center" data-testid={`cmp-store-head-${s.dealership_id}`}>
                  <div className={`${clrs[i]?.band || clrs[0].band} text-white px-3 py-2 font-display font-black text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-1.5 w-full`}>
                    {s.short_name || s.name}
                    {s.net_profit === bestNet && stores.some((x) => x.net_profit !== bestNet) && <Crown size={12} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((mt) => (
              <tr key={mt.key} className={`border-b border-border last:border-b-0 ${mt.hero ? "bg-surface" : ""}`}>
                <td className={`p-3 text-xs ${mt.hero ? "font-display font-bold uppercase text-primary" : "text-text-secondary"}`}>{mt.label}</td>
                {stores.map((s, i) => {
                  const val = s[mt.key] || 0;
                  const heroClr = val >= 0 ? "text-success" : "text-primary";
                  return (
                    <td key={s.dealership_id} className="p-3 text-center" data-testid={`cmp-${mt.key}-${s.dealership_id}`}>
                      <span className={`font-display ${mt.hero ? `font-black text-xl ${heroClr}` : mt.warn ? "font-bold text-warning" : "font-bold"}`}>
                        {mt.fmt(val)}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Financial({ t, fpAlerts, isOwner = false, isTrueOwner = false, showStoresComparison = false, rmvPending = null, onFixRmv, stores = [], activeStoreId = "" }) {
  // Persist the year/month picker so navigating away or a month rolling
  // over never yanks the owner to a different period without their input.
  // Owner explicitly asked: "sempre na virada do mês não faça fechamento
  // automático, deixa que eu faço" — this is the visual counterpart.
  const now = new Date();
  const [year, setYear] = useState(() => {
    const stored = Number(localStorage.getItem("financial_year"));
    return Number.isFinite(stored) && stored >= 2000 ? stored : now.getFullYear();
  });
  const [month, setMonth] = useState(() => {
    const stored = Number(localStorage.getItem("financial_month"));
    return Number.isFinite(stored) && stored >= 1 && stored <= 12 ? stored : now.getMonth() + 1;
  });
  // Persist any manual change so the next visit resumes exactly where the
  // owner left off — even if the calendar month has rolled over.
  useEffect(() => { localStorage.setItem("financial_year", String(year)); }, [year]);
  useEffect(() => { localStorage.setItem("financial_month", String(month)); }, [month]);
  const [closing, setClosing] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [allSold, setAllSold] = useState([]);
  const [soldSearch, setSoldSearch] = useState("");
  const [lost, setLost] = useState({ rows: [], by_reason: [], total_count: 0, total_lost_revenue: 0 });
  const [editing, setEditing] = useState(null); // {} for new expense, expense object for edit
  const [editingCredit, setEditingCredit] = useState(null); // {} for new credit, credit object for edit
  const [opTab, setOpTab] = useState("expenses"); // "expenses" | "credits"
  const [expCarFilter, setExpCarFilter] = useState("all");
  const [detailVid, setDetailVid] = useState(null); // vehicle id to show details for
  const [closings, setClosings] = useState([]);
  const [closingMonth, setClosingMonth] = useState(false); // confirmation modal open
  const [showAllSold, setShowAllSold] = useState(false); // collapsible "all sold cars" history
  // Audit modal (read-only closing integrity check)
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [leadSources, setLeadSources] = useState(null);
  const [storesCmp, setStoresCmp] = useState(null);
  const [moveVid, setMoveVid] = useState(null); // vehicle to move to another store (closing adjustment)
  const otherStores = stores.filter((s) => s.id !== activeStoreId);

  const moveVehicleStore = async (vid, target) => {
    try {
      await api.post(`/vehicles/${vid}/transfer`, {
        target_dealership_id: target.id,
        reason: "Ajuste no fechamento (vendedor mantido)",
      });
      toast.success(`${t("move_store_done")} → ${(target.short_name || target.name || "").trim()}`);
      setMoveVid(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    }
  };

  // Owner-only stores comparison — separate fetch so a 403 for other roles
  // never breaks the main reload chain.
  useEffect(() => {
    if (!showStoresComparison) { setStoresCmp(null); return; }
    let cancel = false;
    api.get("/financial/stores-comparison", { params: { year, month } })
      .then((r) => { if (!cancel) setStoresCmp(r.data); })
      .catch(() => { if (!cancel) setStoresCmp(null); });
    return () => { cancel = true; };
  }, [showStoresComparison, year, month]);

  const reload = async () => {
    try {
      const [c, m, s, l, ar, ls] = await Promise.all([
        api.get("/financial/closing", { params: { year, month } }),
        api.get("/financial/monthly", { params: { months: 6 } }),
        api.get("/financial/sold-vehicles"),
        api.get("/lost-sales", { params: { year, month } }),
        api.get("/financial/closings"),
        api.get("/analytics/lead-sources", { params: { year, month } }).catch(() => ({ data: null })),
      ]);
      setClosing(c.data);
      setMonthly(m.data);
      setAllSold(s.data);
      setLost(l.data);
      setClosings(ar.data || []);
      setLeadSources(ls.data);
    } catch { toast.error(t("error_generic")); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [year, month]);

  const updatePurchasePrice = async (vehicleId, value) => {
    const v = Number(value);
    if (Number.isNaN(v) || v < 0) {
      toast.error(t("error_generic"));
      return;
    }
    try {
      await api.put(`/vehicles/${vehicleId}`, { purchase_price: v });
      toast.success(t("saved"));
      reload();
    } catch { toast.error(t("error_generic")); }
  };

  // Owner-only: lets Alexandre fix the final sold price right from the
  // Financial dashboard whenever a salesperson saved the deal without it.
  const updateSoldPrice = async (vehicleId, value) => {
    const v = Number(value);
    if (Number.isNaN(v) || v < 0) {
      toast.error(t("error_generic"));
      return;
    }
    try {
      await api.put(`/vehicles/${vehicleId}`, { sold_price: v });
      toast.success(t("saved"));
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    }
  };

  // Owner toggles whether this car should be DEFERRED to next month's closing.
  // When deferred, the car is excluded from the current month's profit / commission
  // math and re-appears in next month's closing (useful when the bank hasn't
  // wired the money yet so the dealer hasn't paid the salesperson either).
  const toggleDefer = async (vehicleId, isDeferred) => {
    const msg = isDeferred ? t("defer_undo_confirm") : t("defer_confirm");
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/vehicles/${vehicleId}/defer-closing`);
      toast.success(isDeferred ? t("defer_undone") : t("defer_done"));
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    }
  };

  const removeExpense = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success(t("saved"));
      reload();
    } catch { toast.error(t("error_generic")); }
  };

  const removeCredit = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/credits/${id}`);
      toast.success(t("saved"));
      reload();
    } catch { toast.error(t("error_generic")); }
  };

  // Undo a payment that auto-generated this credit: unmark the receivable installment as paid.
  // The backend cascades — once the installment is unpaid, the linked credit is auto-removed.
  const undoAutoCredit = async (credit) => {
    if (!credit?.receivable_id || !credit?.installment_number) {
      toast.error("Crédito sem vínculo com Recebíveis. Não posso desfazer automaticamente.");
      return;
    }
    if (!window.confirm(
      `Desmarcar a parcela ${credit.installment_number} como NÃO paga? ` +
      `Isso vai remover este crédito e voltar a parcela para pendente em Recebíveis.`
    )) return;
    try {
      await api.post(`/receivables/${credit.receivable_id}/installments/${credit.installment_number}/unpay`);
      toast.success("Pagamento desfeito");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    }
  };

  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    value: i + 1, label: new Date(2000, i, 1).toLocaleString(undefined, { month: "long" }),
  })), []);
  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear() - 4; y <= now.getFullYear() + 1; y++) arr.push(y);
    return arr;
  }, [now]);

  if (!closing) return <p className="text-text-secondary">...</p>;

  const maxNet = Math.max(...monthly.map(m => Math.abs(m.net_profit) || 0), 1);

  return (
    <div data-testid="financial-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="label-eyebrow text-primary mb-2">{t("financial")}</p>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{t("financial_dashboard")}</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {isTrueOwner && (
            <button
              type="button"
              data-testid="daily-summary-send"
              onClick={async () => {
                try {
                  const r = await api.post("/financial/daily-summary/run");
                  r.data.ok ? toast.success(r.data.message) : toast.info(r.data.message);
                } catch (e) { toast.error(e.response?.data?.detail || t("error_generic")); }
              }}
              title={t("daily_summary_btn")}
              className="inline-flex items-center gap-2 border border-border hover:border-primary px-3 h-11 text-[10px] font-display font-bold uppercase tracking-widest text-text-secondary hover:text-primary transition-colors"
            >
              <Mail size={13} /> {t("daily_summary_btn")}
            </button>
          )}
          <select
            data-testid="financial-month"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
          >
            {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            data-testid="financial-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
          >
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {/* "Ir pro mês atual" — quick jump for when the owner wants to
              catch up. NOT automatic: only fires when clicked. */}
          {(year !== now.getFullYear() || month !== now.getMonth() + 1) && (
            <button
              type="button"
              data-testid="financial-jump-current"
              onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); }}
              className="border border-border hover:border-primary hover:text-primary text-xs uppercase tracking-widest px-3 h-11 font-display font-bold transition-colors"
              title="Ir para o mês atual"
            >
              {t("financial_jump_current") || "Mês atual"}
            </button>
          )}
          <button
            type="button"
            data-testid="open-audit"
            onClick={async () => {
              try {
                const r = await api.get("/financial/audit", { params: { year, month } });
                setAuditData(r.data);
                setAuditOpen(true);
              } catch (e) {
                toast.error(e.response?.data?.detail || t("error_generic"));
              }
            }}
            className="border border-warning text-warning hover:bg-warning hover:text-black px-4 h-11 inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest transition-colors"
            title="Auditar o fechamento — mostra tudo que pode estar inflando/deflacionando o lucro"
          >
            <AlertTriangle size={13} /> Auditoria
          </button>
          <button
            type="button"
            data-testid="open-detailed-report"
            onClick={async () => {
              try {
                const r = await api.get("/financial/detailed-report", {
                  params: { year, month },
                  responseType: "blob",
                });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `relatorio-detalhado-${year}-${String(month).padStart(2, "0")}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success(t("detailed_report_generated") || "Relatório gerado");
              } catch (e) {
                toast.error(e.response?.data?.detail || t("error_generic"));
              }
            }}
            className="border border-border hover:border-primary hover:text-primary px-4 h-11 inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest transition-colors"
            title={t("detailed_report_btn") || "Baixar relatório detalhado do mês"}
          >
            <FileText size={13} /> {t("detailed_report_btn") || "Relatório detalhado"}
          </button>
          <button
            type="button"
            data-testid="open-close-month"
            onClick={() => setClosingMonth(true)}
            className="bg-primary hover:bg-primary-hover text-white px-4 h-11 inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest transition-colors"
            title={t("close_month_btn")}
          >
            <Lock size={13} /> {t("close_month_btn")}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border mb-10">
        <div className="bg-background p-6" data-testid="kpi-gross">
          <p className="label-eyebrow mb-3">{t("gross_profit")}</p>
          <p className="font-display font-black text-2xl text-success">{formatCurrency(closing.gross_profit)}</p>
          <p className="text-xs text-text-secondary mt-1">{closing.vehicles_count} {t("cars_sold_in_month").toLowerCase()}</p>
        </div>
        <div className="bg-background p-6" data-testid="kpi-avg-per-car">
          <p className="label-eyebrow mb-3">Lucro médio / carro</p>
          <p className="font-display font-black text-2xl text-success">
            {formatCurrency(closing.vehicles_count > 0 ? closing.gross_profit / closing.vehicles_count : 0)}
          </p>
          <p className="text-xs text-text-secondary mt-1">lucro bruto ÷ carros do mês</p>
        </div>
        <div className="bg-background p-6" data-testid="kpi-opex">
          <p className="label-eyebrow mb-3">{t("operational_total")}</p>
          <p className="font-display font-black text-2xl text-warning">−{formatCurrency(closing.operational_total)}</p>
          <p className="text-xs text-text-secondary mt-1">{closing.operational_expenses.length} {t("expenses_total").toLowerCase()}</p>
        </div>
        <div className="bg-background p-6" data-testid="kpi-commissions">
          <p className="label-eyebrow mb-3">{t("paid_commissions")}</p>
          <p className="font-display font-black text-2xl text-warning">−{formatCurrency(closing.paid_commissions)}</p>
        </div>
        <div className="bg-background p-6" data-testid="kpi-net">
          <p className="label-eyebrow text-primary mb-3">{t("net_profit")}</p>
          <p className={`font-display font-black text-3xl ${closing.net_profit >= 0 ? "text-success" : "text-primary"}`}>
            {formatCurrency(closing.net_profit)}
          </p>
        </div>
      </div>

      {/* Funded cars still missing the RMV — moved here from the Overview (owner request) */}
      {rmvPending && rmvPending.length > 0 && (
        <div data-testid="rmv-pending-panel" className="border border-warning/50 bg-warning/[0.05] mb-10">
          <div className="px-4 py-3 border-b border-warning/30 flex items-center justify-between flex-wrap gap-2">
            <p className="label-eyebrow text-warning inline-flex items-center gap-2">
              <AlertTriangle size={14} /> Carros fundados sem emplacamento ({rmvPending.length})
            </p>
            <p className="text-[10px] uppercase tracking-widest text-text-secondary">Preencha o RMV pro fechamento ficar exato</p>
          </div>
          <div className="divide-y divide-warning/20">
            {rmvPending.slice(0, 8).map((v) => (
              <div key={v.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm font-display font-bold uppercase truncate">
                    {v.year} {v.make} {v.model}
                  </p>
                  {v.store_badge && (
                    <span className={`${v.store_badge.clr} px-1.5 py-0.5 text-[8px] font-display font-black uppercase tracking-[0.15em] rounded-sm shrink-0`}>
                      {v.store_badge.label}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`rmv-fill-${v.id}`}
                  onClick={() => onFixRmv && onFixRmv(v)}
                  className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-warning text-warning hover:bg-warning hover:text-black transition-colors font-display font-bold shrink-0"
                >
                  Preencher RMV
                </button>
              </div>
            ))}
            {rmvPending.length > 8 && (
              <p className="px-4 py-2 text-[10px] text-text-secondary uppercase tracking-widest">+ {rmvPending.length - 8} outros…</p>
            )}
          </div>
        </div>
      )}

      {/* Stores comparison — owner-only (hidden from the whole team) */}
      {showStoresComparison && storesCmp && (
        <StoresComparisonPanel data={storesCmp} monthLabel={monthOptions[month - 1]?.label || ""} />
      )}

      {/* Lead source breakdown — where did the customers come from this month? */}
      <LeadSourcesPanel data={leadSources} monthLabel={monthOptions[month - 1]?.label || ""} t={t} />

      {/* Cars sold in the month */}
      <div className="border border-border mb-10">
        <div className="bg-surface px-4 py-3 border-b border-border flex items-center justify-between">
          <p className="label-eyebrow text-primary">{t("cars_sold_in_month")}</p>
          <p className="text-xs text-text-secondary">
            {t("revenue")}: <span className="font-display font-bold text-white">{formatCurrency(closing.total_revenue)}</span>
            {" · "}
            {t("expenses_total")}: <span className="font-display font-bold text-white">{formatCurrency(closing.total_cost)}</span>
          </p>
        </div>
        {(() => {
          const missingPurchaseCount = closing.vehicles_sold.filter(v => !Number(v.purchase_price)).length;
          if (missingPurchaseCount === 0) return null;
          return (
            <div
              data-testid="missing-purchase-banner"
              className="flex items-start gap-3 px-4 py-3 bg-warning/[0.08] border-b border-warning/40"
            >
              <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
              <div className="text-xs text-warning leading-relaxed">
                <span className="font-display font-bold uppercase tracking-wider">
                  {missingPurchaseCount === 1
                    ? (t("missing_purchase_singular") || "1 carro sem preço de compra")
                    : `${missingPurchaseCount} ${t("missing_purchase_plural") || "carros sem preço de compra"}`}
                </span>
                <span className="text-warning/80 ml-2">
                  {t("missing_purchase_hint") || "— preencha para o fechamento ficar correto. As linhas em destaque estão sem este valor."}
                </span>
              </div>
            </div>
          );
        })()}
        {closing.vehicles_sold.length === 0 && (closing.deferred_out || []).length === 0 ? (
          <p className="text-text-secondary text-sm text-center py-12">—</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr>
                  <th className="text-left p-3 label-eyebrow">{t("make")}/{t("model")}</th>
                  <th className="text-left p-3 label-eyebrow">{t("buyer_name")}</th>
                  <th className="text-left p-3 label-eyebrow">{t("salesperson")}</th>
                  <th className="text-right p-3 label-eyebrow">{t("purchase_price")}</th>
                  <th className="text-right p-3 label-eyebrow">{t("expenses_total")}</th>
                  <th className="text-right p-3 label-eyebrow">{t("sold_price")}</th>
                  <th className="text-right p-3 label-eyebrow">{t("real_profit")}</th>
                  {isOwner && (
                    <th className="text-center p-3 label-eyebrow w-16" title={t("defer_col_hint")}>
                      &nbsp;
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {closing.vehicles_sold.map((v) => {
                  const missingPurchase = !Number(v.purchase_price);
                  return (
                  <tr
                    key={v.vehicle_id}
                    data-testid={`fin-sale-${v.vehicle_id}`}
                    data-missing-purchase={missingPurchase || undefined}
                    className={`border-b transition-colors ${
                      missingPurchase
                        ? "bg-warning/[0.07] border-warning/30 hover:bg-warning/[0.12] border-l-2 border-l-warning"
                        : "border-border hover:bg-surface"
                    }`}
                  >
                    <td className="p-3">
                      <button
                        type="button"
                        data-testid={`open-detail-${v.vehicle_id}`}
                        onClick={() => setDetailVid(v.vehicle_id)}
                        className="flex items-center gap-2 text-left hover:text-primary transition-colors"
                      >
                        {v.image && <img src={v.image} alt="" className="w-10 h-8 object-cover" />}
                        <div>
                          <p className="font-display font-bold border-b border-dashed border-text-secondary/40 inline-flex items-center gap-1.5 flex-wrap">
                            {missingPurchase && (
                              <AlertTriangle size={12} className="text-warning shrink-0" />
                            )}
                            {v.year} {v.make} {v.model}
                            {(v.delivery_step >= 4 || v.delivered_at) && (
                              <span
                                data-testid={`delivered-badge-${v.vehicle_id}`}
                                title={v.delivered_at ? `${t("delivered_on") || "Entregue em"} ${new Date(v.delivered_at).toLocaleDateString()}` : (t("delivered_label") || "Entregue")}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[8.5px] uppercase tracking-widest font-display font-black bg-success text-white"
                              >
                                <Truck size={9} /> {t("delivered_label") || "Entregue"}
                              </span>
                            )}
                          </p>
                          <p className="text-[10px] text-text-secondary uppercase tracking-wider">{t("view_expenses")}</p>
                        </div>
                      </button>
                    </td>
                    <td className="p-3">{v.buyer_name || "—"}</td>
                    <td className="p-3"><NameWithAvatar name={v.salesperson_name} size="sm" /></td>
                    <td className={`p-3 text-right ${missingPurchase ? "text-warning font-display font-bold" : ""}`}>
                      <EditablePrice value={v.purchase_price} onSave={(val) => updatePurchasePrice(v.vehicle_id, val)} testid={`edit-purchase-${v.vehicle_id}`} />
                      {missingPurchase && (
                        <p className="text-[9px] uppercase tracking-widest text-warning mt-1">
                          {t("missing_purchase_row_hint") || "Clique para preencher"}
                        </p>
                      )}
                    </td>
                    <td className="p-3 text-right text-text-secondary">{formatCurrency(v.expenses)}</td>
                    <td className="p-3 text-right font-display font-bold">
                      {isOwner ? (
                        <EditablePrice value={v.sold_price} onSave={(val) => updateSoldPrice(v.vehicle_id, val)} testid={`edit-sold-${v.vehicle_id}`} />
                      ) : (
                        formatCurrency(v.sold_price)
                      )}
                    </td>
                    <td className={`p-3 text-right font-display font-bold ${v.profit >= 0 ? "text-success" : "text-primary"}`}>
                      {formatCurrency(v.profit)}
                    </td>
                    {isOwner && (
                      <td className="p-3 text-center align-middle">
                        <div className="inline-flex items-center gap-1.5">
                          {otherStores.length > 0 && (
                            <button
                              type="button"
                              data-testid={`move-store-btn-${v.vehicle_id}`}
                              onClick={() => setMoveVid(v.vehicle_id)}
                              title={t("move_store_btn")}
                              className="w-8 h-8 inline-flex items-center justify-center border border-border text-text-secondary hover:border-info hover:text-info transition-colors"
                            >
                              <Store size={13} />
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid={`defer-btn-${v.vehicle_id}`}
                            onClick={() => toggleDefer(v.vehicle_id, false)}
                            title={t("defer_to_next_month_tooltip")}
                            className="w-8 h-8 inline-flex items-center justify-center border border-border text-text-secondary hover:border-warning hover:text-warning transition-colors"
                          >
                            <CalendarClock size={13} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
                {/* Deferred-OUT rows — funded this month but moved to another
                    closing (later delivery OR manual defer). Shown muted so
                    the owner remembers they parked those deals. */}
                {(closing.deferred_out || []).map((v) => {
                  const isDelivery = v.reason === "delivery";
                  return (
                  <tr
                    key={`deferred-${v.vehicle_id}`}
                    data-testid={`fin-deferred-${v.vehicle_id}`}
                    className="border-b border-border bg-surface/40 opacity-70"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {v.image && <img src={v.image} alt="" className="w-10 h-8 object-cover grayscale" />}
                        <div>
                          <p className="font-display font-bold text-text-secondary line-through">
                            {v.year} {v.make} {v.model}
                          </p>
                          <p className={`text-[10px] uppercase tracking-widest font-display font-bold inline-flex items-center gap-1 mt-0.5 ${isDelivery ? "text-info" : "text-warning"}`}>
                            {isDelivery ? <Truck size={10} /> : <CalendarClock size={10} />}
                            {isDelivery ? (t("delivered_in_month_label") || "Entregue em") : (t("deferred_to_label") || "Adiado para")}
                            {" "}{v.deferred_to}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-text-secondary">{v.buyer_name || "—"}</td>
                    <td className="p-3 text-text-secondary">
                      <NameWithAvatar name={v.salesperson_name} size="sm" />
                    </td>
                    <td className="p-3 text-right text-text-secondary">—</td>
                    <td className="p-3 text-right text-text-secondary">—</td>
                    <td className="p-3 text-right text-text-secondary line-through">{formatCurrency(v.sold_price)}</td>
                    <td className="p-3 text-right text-text-secondary">—</td>
                    {isOwner && (
                      <td className="p-3 text-center align-middle">
                        {isDelivery ? (
                          /* Auto-migrated due to delivery: no manual revert. */
                          <span className="text-[9px] uppercase tracking-widest text-info inline-flex items-center gap-1" title={t("delivered_auto_migrate_hint") || "Migrou automaticamente pelo mês de entrega"}>
                            <Truck size={10} /> AUTO
                          </span>
                        ) : (
                          <button
                            type="button"
                            data-testid={`undo-defer-${v.vehicle_id}`}
                            onClick={() => toggleDefer(v.vehicle_id, true)}
                            title={t("defer_undo_tooltip")}
                            className="w-8 h-8 inline-flex items-center justify-center border border-warning text-warning hover:bg-warning hover:text-white transition-colors"
                          >
                            <RotateCcw size={13} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Move-to-store modal — closing adjustment, salesperson untouched */}
      {moveVid && createPortal(
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4" onClick={() => setMoveVid(null)}>
          <div
            data-testid="move-store-modal"
            className="bg-surface border border-border w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <p className="label-eyebrow text-primary">{t("move_store_title")}</p>
              <button type="button" onClick={() => setMoveVid(null)} data-testid="move-store-close" className="text-text-secondary hover:text-white transition-colors"><X size={16} /></button>
            </div>
            {(() => {
              const row = (closing?.vehicles_sold || []).find((x) => x.vehicle_id === moveVid);
              return row ? (
                <p className="font-display font-bold text-sm mb-3">{row.year} {row.make} {row.model}{row.salesperson_name ? ` · ${row.salesperson_name}` : ""}</p>
              ) : null;
            })()}
            <div className="space-y-2">
              {otherStores.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`move-store-opt-${s.id}`}
                  onClick={() => moveVehicleStore(moveVid, s)}
                  className="w-full flex items-center gap-2 border border-border px-3 py-2.5 text-sm font-display font-bold uppercase tracking-wide hover:border-info hover:text-info transition-colors"
                >
                  <Store size={14} /> {(s.short_name || s.name || "").trim()}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-text-secondary mt-3 leading-relaxed">{t("move_store_keep_seller")}</p>
          </div>
        </div>,
        document.body
      )}

      {/* All sold cars (all-time) — collapsed by default to keep the page tidy */}
      <div className="border border-border mb-10">
        <button
          type="button"
          data-testid="toggle-all-sold"
          onClick={() => setShowAllSold(s => !s)}
          className="w-full bg-surface px-4 py-3 border-b border-border flex items-center justify-between gap-3 hover:bg-surface/70 transition-colors group"
        >
          <span className="flex items-center gap-3 flex-wrap">
            <History size={16} className="text-primary" />
            <span className="label-eyebrow text-primary">{t("all_sold_cars")}</span>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-border text-text-secondary group-hover:border-primary group-hover:text-primary transition-colors">
              {allSold.length} {t("sales_count").toLowerCase()}
            </span>
            {/* Highlight how many cars still need their purchase_price filled in */}
            {(() => {
              const missingCount = allSold.filter((v) => !Number(v.purchase_price)).length;
              if (missingCount === 0) return null;
              return (
                <span
                  data-testid="missing-purchase-count"
                  className="text-[10px] uppercase tracking-widest px-2 py-1 border border-warning/40 bg-warning/10 text-warning font-display font-bold inline-flex items-center gap-1"
                  title={t("purchase_missing_hint")}
                >
                  <AlertTriangle size={11} /> {missingCount} {t("purchase_missing_count")}
                </span>
              );
            })()}
          </span>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-text-secondary group-hover:text-primary transition-colors">
            {showAllSold ? t("collapse") : t("expand")}
            {showAllSold ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </button>
        {showAllSold && (() => {
          const q = soldSearch.trim().toLowerCase();
          const filteredSold = !q
            ? allSold
            : allSold.filter((v) =>
                (v.buyer_name || "").toLowerCase().includes(q) ||
                (v.vin || "").toLowerCase().includes(q) ||
                `${v.year} ${v.make} ${v.model}`.toLowerCase().includes(q)
              );
          return (
          <>
            {/* Lupa — busca por nome do cliente, VIN ou carro */}
            <div className="px-4 py-3 border-b border-border bg-surface/50 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[260px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
                <input
                  data-testid="sold-search"
                  className="w-full bg-background border border-border focus:border-primary focus:outline-none pl-9 pr-8 h-10 text-sm"
                  placeholder={t("sold_search_placeholder") || "Buscar por nome do cliente, VIN ou carro..."}
                  value={soldSearch}
                  onChange={(e) => setSoldSearch(e.target.value)}
                />
                {soldSearch && (
                  <button
                    type="button"
                    data-testid="sold-search-clear"
                    onClick={() => setSoldSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-primary"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {q && (
                <span data-testid="sold-search-count" className="text-[10px] uppercase tracking-widest text-text-secondary">
                  {filteredSold.length} {t("results_label") || "resultado(s)"}
                </span>
              )}
            </div>
            {allSold.length === 0 ? (
            <p className="text-text-secondary text-sm text-center py-12">—</p>
          ) : filteredSold.length === 0 ? (
            <p data-testid="sold-search-empty" className="text-text-secondary text-sm text-center py-12">
              {t("sold_search_empty") || "Nenhuma venda encontrada pra essa busca"}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-3 label-eyebrow">{t("sale_date")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("make")}/{t("model")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("buyer_name")}</th>
                    <th className="text-right p-3 label-eyebrow">{t("purchase_price")}</th>
                    <th className="text-right p-3 label-eyebrow">{t("sold_price")}</th>
                    <th className="text-right p-3 label-eyebrow">{t("real_profit")}</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSold.map((v) => {
                    // Highlight rows with zero purchase_price so the owner can spot
                    // which cars still need their cost filled in (otherwise the
                    // "lucro real" column overstates profit).
                    const missingPurchase = !Number(v.purchase_price);
                    return (
                    <tr
                      key={v.vehicle_id}
                      data-testid={`fin-all-${v.vehicle_id}`}
                      data-missing-purchase={missingPurchase || undefined}
                      className={`border-b border-border hover:bg-surface transition-colors ${
                        missingPurchase
                          ? "bg-warning/[0.04] hover:bg-warning/[0.07] ring-1 ring-inset ring-warning/20"
                          : ""
                      }`}
                    >
                      <td className="p-3 text-xs text-text-secondary font-mono">
                        {v.sold_at ? new Date(v.sold_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          data-testid={`open-detail-all-${v.vehicle_id}`}
                          onClick={() => setDetailVid(v.vehicle_id)}
                          className="flex items-center gap-2 text-left hover:text-primary transition-colors"
                        >
                          {v.image && <img src={v.image} alt="" className="w-10 h-8 object-cover" />}
                          <div>
                            <p className="font-display font-bold border-b border-dashed border-text-secondary/40 inline-flex items-center gap-2">
                              {v.year} {v.make} {v.model}
                              {(v.delivery_step >= 4 || v.delivered_at) && (
                                <span
                                  data-testid={`delivered-badge-${v.vehicle_id}`}
                                  title={v.delivered_at ? `${t("delivered_on") || "Entregue em"} ${new Date(v.delivered_at).toLocaleDateString()}` : (t("delivered_label") || "Entregue")}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[8.5px] uppercase tracking-widest font-display font-black bg-success text-white"
                                >
                                  <Truck size={9} /> {t("delivered_label") || "Entregue"}
                                </span>
                              )}
                            </p>
                            <p className="text-[10px] text-text-secondary uppercase tracking-wider">{t("view_expenses")}</p>
                          </div>
                        </button>
                      </td>
                      <td className="p-3">
                        <p>{v.buyer_name || "—"}</p>
                        {v.vin && <p className="text-[9px] font-mono text-text-secondary uppercase tracking-wider mt-0.5">VIN {v.vin}</p>}
                      </td>
                      <td className={`p-3 text-right ${missingPurchase ? "relative" : ""}`}>
                        <div className="inline-flex items-center gap-2">
                          {missingPurchase && (
                            <span
                              className="text-[8.5px] uppercase tracking-widest font-display font-black px-1.5 py-0.5 bg-warning text-black inline-flex items-center gap-1"
                              title={t("purchase_missing_hint")}
                            >
                              <AlertTriangle size={9} /> {t("purchase_missing_badge")}
                            </span>
                          )}
                          <EditablePrice
                            value={v.purchase_price}
                            onSave={(val) => updatePurchasePrice(v.vehicle_id, val)}
                            testid={`edit-purchase-all-${v.vehicle_id}`}
                          />
                        </div>
                      </td>
                      <td className="p-3 text-right font-display font-bold">
                        {isOwner ? (
                          <EditablePrice value={v.sold_price} onSave={(val) => updateSoldPrice(v.vehicle_id, val)} testid={`edit-sold-all-${v.vehicle_id}`} />
                        ) : (
                          formatCurrency(v.sold_price)
                        )}
                      </td>
                      <td className={`p-3 text-right font-display font-bold ${v.profit >= 0 ? "text-success" : "text-primary"}`}>
                        {formatCurrency(v.profit)}
                      </td>
                      <td className="p-3 text-right">
                        {isOwner && (
                          <button
                            type="button"
                            data-testid={`resend-sale-email-${v.vehicle_id}`}
                            title={t("resend_sale_email")}
                            onClick={async () => {
                              try {
                                const r = await api.post(`/vehicles/${v.vehicle_id}/resend-sale-email`);
                                (r.data?.ok ? toast.success : toast.warning)(r.data?.message || "");
                              } catch (e) {
                                toast.error(e.response?.data?.detail || t("error_generic"));
                              }
                            }}
                            className="w-7 h-7 border border-border hover:border-primary hover:text-primary inline-flex items-center justify-center transition-colors"
                          >
                            <Mail size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          </>
          );
        })()}
      </div>

      {/* Lost sales (when client backed out) */}
      {(lost.total_count > 0 || lost.rows.length > 0) && (
        <div className="border border-border mb-10">
          <div className="bg-surface px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
            <p className="label-eyebrow text-warning">{t("lost_sales_title")}</p>
            <p className="text-xs text-text-secondary">
              {lost.total_count} {t("sales_count").toLowerCase()} ·
              <span className="ml-1">{t("lost_revenue")}: <span className="font-display font-bold text-warning">{formatCurrency(lost.total_lost_revenue)}</span></span>
            </p>
          </div>
          {lost.by_reason.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border border-b border-border">
              {lost.by_reason.map((b) => (
                <div key={b.reason} className="bg-background p-3" data-testid={`lost-reason-${b.reason}`}>
                  <p className="label-eyebrow text-[9px] mb-1 truncate">{t(`reason_${b.reason}`) || b.reason}</p>
                  <p className="font-display font-black text-lg text-warning">{b.count}</p>
                  <p className="text-[10px] text-text-secondary">{formatCurrency(b.lost_revenue)}</p>
                </div>
              ))}
            </div>
          )}
          {lost.rows.length === 0 ? (
            <p className="text-text-secondary text-sm text-center py-8">—</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-3 label-eyebrow">{t("sale_date")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("make")}/{t("model")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("buyer_name")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("salesperson")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("lost_reason")}</th>
                    <th className="text-right p-3 label-eyebrow">{t("sold_price")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lost.rows.map((r) => (
                    <tr key={r.id} data-testid={`lost-row-${r.id}`} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="p-3 text-xs text-text-secondary font-mono">
                        {r.lost_at ? new Date(r.lost_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {r.image && <img src={r.image} alt="" className="w-10 h-8 object-cover" />}
                          <p className="font-display font-bold">{r.year} {r.make} {r.model}</p>
                        </div>
                      </td>
                      <td className="p-3">{r.buyer_name || "—"}</td>
                      <td className="p-3"><NameWithAvatar name={r.salesperson_name} size="sm" /></td>
                      <td className="p-3">
                        <div>
                          <span className="text-xs uppercase tracking-wider px-2 py-1 border border-warning/40 text-warning">
                            {t(`reason_${r.reason}`) || r.reason}
                          </span>
                          {r.notes && <p className="text-[11px] text-text-secondary mt-1 italic">"{r.notes}"</p>}
                        </div>
                      </td>
                      <td className="p-3 text-right font-display font-bold text-warning">
                        {isOwner ? (
                          <EditablePrice value={r.sold_price} onSave={(val) => updateSoldPrice(r.vehicle_id, val)} testid={`edit-sold-sp-${r.vehicle_id}`} />
                        ) : (
                          formatCurrency(r.sold_price)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Operational expenses + credits (tabbed) */}
      <div className="border border-border mb-10">
        <div className="bg-surface px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3">
          {/* Tab pills */}
          <div className="flex gap-2">
            <button
              data-testid="op-tab-expenses"
              onClick={() => setOpTab("expenses")}
              className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
                opTab === "expenses" ? "bg-warning border-warning text-black" : "border-border text-text-secondary hover:border-warning hover:text-warning"
              }`}
            >
              − {t("operational_expenses")} ({(closing.operational_expenses || []).length})
            </button>
            <button
              data-testid="op-tab-credits"
              onClick={() => setOpTab("credits")}
              className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
                opTab === "credits" ? "bg-success border-success text-white" : "border-border text-text-secondary hover:border-success hover:text-success"
              }`}
            >
              + {t("operational_credits")} ({(closing.operational_credits || []).length})
            </button>
          </div>

          {/* Add buttons */}
          {opTab === "expenses" ? (
            <button
              data-testid="add-expense"
              onClick={() => setEditing({})}
              className="bg-primary hover:bg-primary-hover px-4 py-2 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2"
            >
              <Plus size={14} /> {t("add_expense")}
            </button>
          ) : (
            <button
              data-testid="add-credit"
              onClick={() => setEditingCredit({})}
              className="bg-success hover:opacity-90 text-white px-4 py-2 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2"
            >
              <Plus size={14} /> {t("add_credit")}
            </button>
          )}
        </div>

        {/* Net summary bar — shows credit reduces expense */}
        <div className="bg-background/40 px-4 py-2 border-b border-border flex flex-wrap items-center justify-between gap-3 text-xs">
          <span className="text-text-secondary uppercase tracking-widest">
            {t("operational_total")}: <span className="font-display font-bold text-warning">−{formatCurrency(closing.operational_total)}</span>
            {" · "}
            {t("operational_credits")}: <span className="font-display font-bold text-success">+{formatCurrency(closing.credits_total || 0)}</span>
          </span>
          <span className="font-display font-bold uppercase tracking-widest">
            {t("net_operational")}: <span className="text-primary">−{formatCurrency(closing.operational_net || closing.operational_total)}</span>
          </span>
        </div>

        {/* Expenses table */}
        {opTab === "expenses" && (() => {
          const allExp = closing.operational_expenses || [];
          const linkedCars = Array.from(new Map(allExp.filter(e => e.vehicle_id).map(e => [e.vehicle_id, e.vehicle_label])).entries());
          const activeFilter = expCarFilter !== "all" && linkedCars.some(([vid]) => vid === expCarFilter) ? expCarFilter : "all";
          const expRows = activeFilter === "all" ? allExp : allExp.filter(e => e.vehicle_id === activeFilter);
          return allExp.length === 0 ? (
            <p className="text-text-secondary text-sm text-center py-12">{t("no_expenses")}</p>
          ) : (
            <div className="overflow-x-auto">
              {linkedCars.length > 0 && (
                <div className="px-4 pt-3 flex items-center gap-2">
                  <label className="label-eyebrow">Filtrar por carro:</label>
                  <select
                    data-testid="exp-car-filter"
                    value={activeFilter}
                    onChange={(e) => setExpCarFilter(e.target.value)}
                    className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-9 text-xs max-w-[320px]"
                  >
                    <option value="all">Todos ({allExp.length})</option>
                    {linkedCars.map(([vid, label]) => (
                      <option key={vid} value={vid}>{label}</option>
                    ))}
                  </select>
                  {activeFilter !== "all" && (
                    <span className="text-xs font-display font-bold text-warning">
                      −{formatCurrency(expRows.reduce((s, e) => s + (Number(e.amount) || 0), 0))}
                    </span>
                  )}
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-3 label-eyebrow">{t("expense_date")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("expense_category")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("expense_description")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("expense_attachment")}</th>
                    <th className="text-right p-3 label-eyebrow">{t("expense_amount")}</th>
                    <th className="text-right p-3 label-eyebrow"></th>
                  </tr>
                </thead>
                <tbody>
                  {expRows.map((e) => (
                    <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="p-3 font-mono text-xs">{e.date}</td>
                      <td className="p-3">
                        <span className="text-xs uppercase tracking-wider px-2 py-1 border border-border">
                          {t(`cat_${e.category}`) || e.category}
                        </span>
                      </td>
                      <td className="p-3">
                        {e.description || "—"}
                        {e.vehicle_label && (
                          <p className="text-[10px] text-primary mt-0.5 font-display font-bold uppercase tracking-wider">🚗 {e.vehicle_label}</p>
                        )}
                      </td>
                      <td className="p-3">
                        {e.attachment_url ? (
                          <a href={e.attachment_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs">
                            <Paperclip size={12} /> {t("view")}
                          </a>
                        ) : "—"}
                      </td>
                      <td className="p-3 text-right font-display font-bold text-warning">−{formatCurrency(e.amount)}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1">
                          <button data-testid={`edit-expense-${e.id}`} onClick={() => setEditing(e)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><Edit2 size={14} /></button>
                          <button data-testid={`del-expense-${e.id}`} onClick={() => removeExpense(e.id)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Credits table */}
        {opTab === "credits" && (
          (closing.operational_credits || []).length === 0 ? (
            <p className="text-text-secondary text-sm text-center py-12">{t("no_credits")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-3 label-eyebrow">{t("expense_date")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("expense_category")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("expense_description")}</th>
                    <th className="text-left p-3 label-eyebrow">{t("expense_attachment")}</th>
                    <th className="text-right p-3 label-eyebrow">{t("expense_amount")}</th>
                    <th className="text-right p-3 label-eyebrow"></th>
                  </tr>
                </thead>
                <tbody>
                  {closing.operational_credits.map((c) => (
                    <tr key={c.id} data-testid={`credit-row-${c.id}`} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="p-3 font-mono text-xs">{c.date}</td>
                      <td className="p-3">
                        <span className="text-xs uppercase tracking-wider px-2 py-1 border border-success/40 text-success bg-success/5">
                          {t(`credit_cat_${c.category}`) || c.category}
                        </span>
                        {c.auto && (
                          <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-info/40 text-info bg-info/5" title={t("credit_auto_tooltip")}>
                            ⚡ {t("credit_auto")}
                          </span>
                        )}
                      </td>
                      <td className="p-3">{c.description || "—"}</td>
                      <td className="p-3">
                        {c.attachment_url ? (
                          <a href={c.attachment_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs">
                            <Paperclip size={12} /> {t("view")}
                          </a>
                        ) : "—"}
                      </td>
                      <td className="p-3 text-right font-display font-bold text-success">+{formatCurrency(c.amount)}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1 items-center">
                          {c.auto ? (
                            <>
                              <span
                                className="text-[10px] text-text-secondary uppercase tracking-widest px-2 py-1 border border-info/40 bg-info/5"
                                title={t("credit_auto_tooltip")}
                              >
                                {t("credit_managed_in_receivables")}
                              </span>
                              <button
                                type="button"
                                data-testid={`undo-credit-${c.id}`}
                                onClick={() => undoAutoCredit(c)}
                                title="Desfazer pagamento — marca a parcela como NÃO paga em Recebíveis"
                                className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
                              >
                                <RotateCcw size={14} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button data-testid={`edit-credit-${c.id}`} onClick={() => setEditingCredit(c)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><Edit2 size={14} /></button>
                              <button data-testid={`del-credit-${c.id}`} onClick={() => removeCredit(c.id)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><Trash2 size={14} /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Last 6 months net profit chart */}
      <div className="border border-border p-6">
        <p className="label-eyebrow text-primary mb-6">{t("monthly_closing")} · 6m</p>
        <div className="space-y-3">
          {monthly.map((m) => {
            const w = (Math.abs(m.net_profit) / maxNet) * 100;
            const positive = m.net_profit >= 0;
            return (
              <div key={m.label} className="flex items-center gap-4">
                <span className="font-display font-bold text-sm w-20">{m.label}</span>
                <div className="flex-1 bg-surface h-8 relative overflow-hidden">
                  <div className={`absolute inset-y-0 left-0 ${positive ? "bg-success" : "bg-primary"}`} style={{ width: `${w}%` }} />
                </div>
                <span className={`font-display font-bold text-sm w-32 text-right ${positive ? "text-success" : "text-primary"}`}>
                  {formatCurrency(m.net_profit)}
                </span>
                <span className="text-xs text-text-secondary w-24 text-right">{m.vehicles_count} {t("sales_count").toLowerCase()}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floor Plans calendar */}
      <div className="mt-12 mb-6" data-testid="floor-plans-wrap">
        {fpAlerts && fpAlerts.total > 0 && (
          <FloorPlanAlertBanner alerts={fpAlerts} t={t} compact />
        )}
        <FloorPlans t={t} />
      </div>

      {/* Closings archive (folder of all monthly closings) */}
      <div className="border border-border mt-12 mb-10" data-testid="closings-archive">
        <div className="bg-surface px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <FolderArchive size={16} className="text-primary" />
            <p className="label-eyebrow">{t("closings_archive_title")}</p>
          </div>
          <p className="text-xs text-text-secondary">{closings.length} {closings.length === 1 ? t("closing_one") : t("closing_many")}</p>
        </div>
        {closings.length === 0 ? (
          <p className="text-text-secondary text-sm text-center py-8 italic">{t("closings_archive_empty")}</p>
        ) : (
          <>
            <ClosingsBarChart closings={closings} t={t} />
            <div className="divide-y divide-border">
            {closings.map(c => {
              const label = `${monthOptions[c.month - 1]?.label} ${c.year}`;
              const downloadUrl = `${api.defaults.baseURL}/financial/closings/${c.id}/pdf`;
              const removeOne = async () => {
                if (!window.confirm(t("closings_confirm_delete"))) return;
                try {
                  await api.delete(`/financial/closings/${c.id}`);
                  toast.success(t("saved"));
                  reload();
                } catch { toast.error(t("error_generic")); }
              };
              return (
                <div key={c.id} data-testid={`closing-${c.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-display font-bold uppercase">{label}</p>
                    <p className="text-[11px] text-text-secondary">
                      {t("closed_at")} {new Date(c.closed_at).toLocaleString("pt-BR")} · {c.closed_by}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-text-secondary uppercase">{t("net_profit")}</p>
                    <p className={`font-display font-black text-base ${c.net_profit >= 0 ? "text-success" : "text-primary"}`}>
                      {formatCurrency(c.net_profit)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-text-secondary uppercase">{t("vehicles")}</p>
                    <p className="font-display font-bold">{c.vehicles_count}</p>
                  </div>
                  <div className="inline-flex gap-1">
                    <button
                      type="button"
                      data-testid={`email-closing-${c.id}`}
                      onClick={async () => {
                        try {
                          const r = await api.post(`/financial/closing-email/run?year=${c.year}&month=${c.month}`);
                          (r.data?.ok ? toast.success : toast.warning)(r.data?.message || "");
                        } catch (err) { toast.error(err.response?.data?.detail || t("error_generic")); }
                      }}
                      className="w-9 h-9 border border-border hover:border-success hover:text-success flex items-center justify-center transition-colors"
                      title={t("send_closing_email")}
                    >
                      <Mail size={13} />
                    </button>
                    <a
                      href={`${downloadUrl}?token=${encodeURIComponent(localStorage.getItem("auth_token") || "")}`}
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const r = await api.get(`/financial/closings/${c.id}/pdf`, { responseType: "blob" });
                          const url = URL.createObjectURL(r.data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `fechamento-${c.year}-${String(c.month).padStart(2, "0")}.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch { toast.error(t("error_generic")); }
                      }}
                      data-testid={`download-${c.id}`}
                      className="w-9 h-9 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
                      title={t("download")}
                    >
                      <Download size={13} />
                    </a>
                    <button
                      type="button"
                      data-testid={`delete-closing-${c.id}`}
                      onClick={removeOne}
                      className="w-9 h-9 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
                      title={t("delete")}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {closingMonth && (
        <CloseMonthModal
          year={year}
          month={month}
          monthLabel={monthOptions[month - 1]?.label || ""}
          closing={closing}
          t={t}
          onClose={() => setClosingMonth(false)}
          onDone={() => { setClosingMonth(false); reload(); }}
        />
      )}

      {auditOpen && auditData && (
        <FinancialAuditModal
          data={auditData}
          monthLabel={monthOptions[month - 1]?.label || ""}
          onClose={() => { setAuditOpen(false); setAuditData(null); }}
        />
      )}

      {editing && (
        <ExpenseForm
          expense={editing.id ? editing : null}
          t={t}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      {editingCredit && (
        <CreditForm
          credit={editingCredit.id ? editingCredit : null}
          t={t}
          onClose={() => setEditingCredit(null)}
          onSaved={() => { setEditingCredit(null); reload(); }}
        />
      )}

      {detailVid && (
        <VehicleExpensesModal
          vehicleId={detailVid}
          t={t}
          canCorrect={isTrueOwner}
          onClose={() => setDetailVid(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function ExpenseForm({ expense, t, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(expense || { date: today, category: "other", description: "", amount: 0, attachment_url: "", attachment_public_id: "", vehicle_id: "" });
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [carOptions, setCarOptions] = useState([]);
  const [carSearch, setCarSearch] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/expenses/vehicle-options").then((r) => setCarOptions(r.data || [])).catch(() => {});
  }, []);

  const carLabel = (c) => `${c.year || ""} ${c.make || ""} ${c.model || ""}`.trim() + (c.vin ? ` · VIN ${c.vin}` : "") + (c.status === "sold" ? " (VENDIDO)" : "");
  const filteredCars = carSearch
    ? carOptions.filter((c) => carLabel(c).toLowerCase().includes(carSearch.toLowerCase()))
    : carOptions;

  const onFileSelected = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t("file_too_large"));
      return;
    }
    try {
      const sigRes = await api.get("/cloudinary/signature", { params: { folder: `vehicles/expenses/` } });
      const sig = sigRes.data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/auto/upload`, { method: "POST", body: fd });
      const json = await res.json();
      if (!json.secure_url) throw new Error(json.error?.message || "upload failed");
      set("attachment_url", json.secure_url);
      set("attachment_public_id", json.public_id);
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err.message || t("error_generic"));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const payload = { ...form, amount: Math.round(Number(form.amount) || 0), vehicle_id: form.vehicle_id || "" };
    try {
      if (expense) await api.put(`/expenses/${expense.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={save} className="bg-background border border-border w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase tracking-tight">
            {expense ? t("edit") : t("add_expense")}
          </h2>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t("expense_date")} type="date" value={form.date} set={(v) => set("date", v)} required testid="exp-date" />
          <div>
            <label className="label-eyebrow block mb-2">{t("expense_category")}</label>
            <select
              data-testid="exp-category"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}
            </select>
          </div>
        </div>
        <Input label={t("expense_description")} value={form.description} set={(v) => set("description", v)} testid="exp-description" />
        <div>
          <label className="label-eyebrow block mb-2">Carro vinculado (opcional) — inclui vendidos</label>
          <input
            data-testid="exp-car-search"
            type="text"
            placeholder="Buscar por modelo ou VIN..."
            value={carSearch}
            onChange={(e) => setCarSearch(e.target.value)}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-9 text-xs mb-2"
          />
          <select
            data-testid="exp-vehicle"
            value={form.vehicle_id || ""}
            onChange={(e) => set("vehicle_id", e.target.value)}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
          >
            <option value="">— Nenhum (despesa geral) —</option>
            {form.vehicle_id && !filteredCars.some((c) => c.id === form.vehicle_id) && (
              <option value={form.vehicle_id}>{form.vehicle_label || expense?.vehicle_label || "Carro vinculado"}</option>
            )}
            {filteredCars.map((c) => (
              <option key={c.id} value={c.id}>{carLabel(c)}</option>
            ))}
          </select>
          {form.vehicle_id && (
            <p className="text-[10px] text-text-secondary mt-1.5">Essa despesa também entra nos Gastos Detalhados do carro</p>
          )}
        </div>
        <div>
          <Input label={t("expense_amount")} type="number" value={form.amount} set={(v) => set("amount", v)} required testid="exp-amount" />
          {wholeDollarHint(form.amount) && (
            <p data-testid="exp-round-hint" className="text-[10px] text-warning mt-1.5">⚠ {wholeDollarHint(form.amount)}</p>
          )}
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("expense_attachment")}</label>
          <div className="flex items-center gap-3">
            <label data-testid="exp-attachment-label" className="cursor-pointer border border-border hover:border-primary px-3 h-11 inline-flex items-center gap-2 text-xs uppercase tracking-wider">
              <Paperclip size={14} /> {t("expense_attachment")}
              <input data-testid="exp-attachment" type="file" accept="image/*,application/pdf" onChange={(e) => onFileSelected(e.target.files?.[0])} className="hidden" />
            </label>
            {form.attachment_url && (
              <a href={form.attachment_url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1">
                {form.attachment_url.match(/\.(pdf)$/i) ? <FileText size={14} /> : <ImageIcon size={14} />} {t("view")}
              </a>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="submit" data-testid="exp-submit" disabled={saving} className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
            <Check size={14} /> {saving ? "..." : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function CreditForm({ credit, t, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const CREDIT_CATEGORIES = ["sublease", "refund", "commission", "other"];
  const [form, setForm] = useState(credit || { date: today, category: "sublease", description: "", amount: 0, attachment_url: "", attachment_public_id: "" });
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFileSelected = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error(t("file_too_large")); return; }
    try {
      const sig = (await api.get("/cloudinary/signature", { params: { folder: `vehicles/expenses/` } })).data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/auto/upload`, { method: "POST", body: fd });
      const json = await res.json();
      if (!json.secure_url) throw new Error(json.error?.message || "upload failed");
      set("attachment_url", json.secure_url);
      set("attachment_public_id", json.public_id);
    } catch (err) {
      toast.error(err.message || t("error_generic"));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const payload = { ...form, amount: Math.round(Number(form.amount) || 0) };
    try {
      if (credit) await api.put(`/credits/${credit.id}`, payload);
      else await api.post("/credits", payload);
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={save} className="bg-background border border-success w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase tracking-tight inline-flex items-center gap-2">
            <Plus size={18} className="text-success" /> {credit ? t("edit_credit") : t("add_credit")}
          </h2>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t("expense_date")} type="date" value={form.date} set={(v) => set("date", v)} required testid="cr-date" />
          <div>
            <label className="label-eyebrow block mb-2">{t("expense_category")}</label>
            <select
              data-testid="cr-category"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            >
              {CREDIT_CATEGORIES.map(c => <option key={c} value={c}>{t(`credit_cat_${c}`)}</option>)}
            </select>
          </div>
        </div>
        <Input label={t("expense_description")} value={form.description} set={(v) => set("description", v)} testid="cr-description" />
        <div>
          <Input label={t("expense_amount")} type="number" value={form.amount} set={(v) => set("amount", v)} required testid="cr-amount" />
          {wholeDollarHint(form.amount) && (
            <p data-testid="cr-round-hint" className="text-[10px] text-warning mt-1.5">⚠ {wholeDollarHint(form.amount)}</p>
          )}
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("expense_attachment")}</label>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer border border-border hover:border-primary px-3 h-11 inline-flex items-center gap-2 text-xs uppercase tracking-wider">
              <Paperclip size={14} /> {t("expense_attachment")}
              <input data-testid="cr-attachment" type="file" accept="image/*,application/pdf" onChange={(e) => onFileSelected(e.target.files?.[0])} className="hidden" />
            </label>
            {form.attachment_url && (
              <a href={form.attachment_url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1">
                {form.attachment_url.match(/\.(pdf)$/i) ? <FileText size={14} /> : <ImageIcon size={14} />} {t("view")}
              </a>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="submit" data-testid="cr-submit" disabled={saving} className="bg-success hover:opacity-90 text-white disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
            <Check size={14} /> {saving ? "..." : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}


function VehicleExpensesModal({ vehicleId, t, onClose, onChanged, canCorrect = false }) {
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revertOpen, setRevertOpen] = useState(false);
  // Owner-only inline correction of sold_price / purchase_price (fixes wrong
  // numbers typed by salespeople so the monthly closing stays exact).
  const [fixing, setFixing] = useState(false);
  const [fixSold, setFixSold] = useState("");
  const [fixPurchase, setFixPurchase] = useState("");
  const [savingFix, setSavingFix] = useState(false);
  // Inline form to add post-sale expenses (warranty repairs, late
  // detailing, etc.) directly from this detail modal. Keeps the dealer
  // from having to drop into the inventory page and edit the car.
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/vehicles/${vehicleId}`);
      setVehicle(r.data);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [vehicleId]);

  const items = vehicle?.expense_items || [];
  const expensesTotal = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const sold = Number(vehicle?.sold_price) || 0;
  const purchase = Number(vehicle?.purchase_price) || 0;
  const commissionPaid = vehicle?.commission_paid ? Number(vehicle?.commission_amount) || 0 : 0;
  const liveSold = fixing ? (Number(fixSold) || 0) : sold;
  const livePurchase = fixing ? (Number(fixPurchase) || 0) : purchase;
  const profit = liveSold - livePurchase - expensesTotal - commissionPaid;

  const startFix = () => {
    setFixSold(String(sold || ""));
    setFixPurchase(String(purchase || ""));
    setFixing(true);
  };

  const saveFix = async () => {
    setSavingFix(true);
    try {
      await api.put(`/vehicles/${vehicleId}/sale-correction`, {
        sold_price: Number(fixSold) || 0,
        purchase_price: Number(fixPurchase) || 0,
      });
      toast.success(t("vex_fix_saved") || "Valores corrigidos");
      setFixing(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    } finally {
      setSavingFix(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <div className="bg-background border border-border w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <p className="label-eyebrow text-primary mb-1">{t("real_profit")}</p>
            <h2 className="font-display font-black text-xl uppercase tracking-tight">
              {vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : "..."}
            </h2>
            {vehicle?.buyer_name && <p className="text-xs text-text-secondary mt-1">{t("buyer_name")}: {vehicle.buyer_name}</p>}
          </div>
          <button onClick={onClose} data-testid="close-detail"><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-4 bg-surface animate-pulse" style={{ width: `${88 - i * 9}%` }} />
            ))}
          </div>
        ) : (
          <>
            {/* Summary breakdown */}
            <div className="p-6 space-y-2 border-b border-border">
              {canCorrect && !fixing && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    data-testid="vex-fix-values"
                    onClick={startFix}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 border border-warning/50 bg-warning/10 text-warning hover:bg-warning hover:text-black transition-colors font-display font-bold"
                  >
                    <Edit2 size={11} /> {t("vex_fix_values") || "Corrigir valores (só dono)"}
                  </button>
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-secondary">{t("sold_price")}</span>
                {fixing ? (
                  <input
                    data-testid="vex-fix-sold"
                    type="number"
                    step="0.01"
                    value={fixSold}
                    onChange={(e) => setFixSold(e.target.value)}
                    className="w-36 bg-surface border border-warning px-2 py-1 text-right font-display font-bold text-sm focus:outline-none"
                  />
                ) : (
                  <span className="font-display font-bold">{formatCurrency(sold)}</span>
                )}
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-secondary">− {t("purchase_price")}</span>
                {fixing ? (
                  <input
                    data-testid="vex-fix-purchase"
                    type="number"
                    step="0.01"
                    value={fixPurchase}
                    onChange={(e) => setFixPurchase(e.target.value)}
                    className="w-36 bg-surface border border-warning px-2 py-1 text-right font-display font-bold text-sm focus:outline-none"
                  />
                ) : (
                  <span className="font-display font-bold">{formatCurrency(purchase)}</span>
                )}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">− {t("expenses_total")}</span>
                <span className="font-display font-bold">{formatCurrency(expensesTotal)}</span>
              </div>
              {commissionPaid > 0 && (
                <div className="flex justify-between text-sm" data-testid="modal-commission-line">
                  <span className="text-text-secondary">− {t("paid_commissions")} ({vehicle?.salesperson_name || "—"})</span>
                  <span className="font-display font-bold">{formatCurrency(commissionPaid)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-border">
                <span className="label-eyebrow text-primary">{t("real_profit")}</span>
                <span className={`font-display font-black text-2xl ${profit >= 0 ? "text-success" : "text-primary"}`}>
                  {formatCurrency(profit)}
                </span>
              </div>
              {fixing && (
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-warning/40 bg-warning/[0.04] -mx-6 px-6 pb-1">
                  <p className="text-[11px] text-warning flex-1">
                    {t("vex_fix_hint") || "Correção do dono: ajusta direto o fechamento. Fica registrado no histórico do carro."}
                  </p>
                  <button
                    type="button"
                    data-testid="vex-fix-cancel"
                    onClick={() => setFixing(false)}
                    className="border border-border px-3 py-2 text-[10px] font-display font-bold uppercase tracking-widest hover:border-primary transition-colors"
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="button"
                    data-testid="vex-fix-save"
                    disabled={savingFix}
                    onClick={saveFix}
                    className="bg-warning text-black px-4 py-2 text-[10px] font-display font-bold uppercase tracking-widest disabled:opacity-50 hover:opacity-90 transition-opacity inline-flex items-center gap-1.5"
                  >
                    <Check size={12} /> {savingFix ? "..." : (t("vex_fix_save") || "Salvar correção")}
                  </button>
                </div>
              )}
            </div>

            {/* Itemized expenses */}
            <div className="p-6">
              <p className="label-eyebrow text-primary mb-4">
                {t("expenses_total")} · {items.length + (commissionPaid > 0 ? 1 : 0)}
              </p>
              {items.length === 0 && commissionPaid === 0 ? (
                <p className="text-text-secondary text-sm text-center py-8 border border-dashed border-border">
                  {t("no_expenses")}
                </p>
              ) : (
                <div className="border border-border">
                  {commissionPaid > 0 && (
                    <div data-testid="vex-commission" className="flex items-center justify-between gap-3 p-3 border-b border-border bg-success/5">
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-bold text-sm truncate">{t("paid_commissions")}</p>
                        <p className="text-xs text-text-secondary">
                          {vehicle?.salesperson_name || "—"}{vehicle?.delivered_at ? ` · ${new Date(vehicle.delivered_at).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                      <span className="font-display font-bold text-warning shrink-0 w-28 text-right">
                        −{formatCurrency(commissionPaid)}
                      </span>
                    </div>
                  )}
                  {items.map((it, i) => {
                    const att = (it.attachments && it.attachments[0]) || null;
                    return (
                      <div key={it.id || i} data-testid={`vex-item-${i}`} className="flex items-center justify-between gap-3 p-3 border-b border-border last:border-b-0">
                        <div className="min-w-0 flex-1">
                          <p className="font-display font-bold text-sm truncate">{it.description || "—"}</p>
                          <p className="text-xs text-text-secondary">
                            {it.category || "—"}{it.date ? ` · ${it.date}` : ""}
                          </p>
                        </div>
                        {att?.url && (
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
                          >
                            {(att.url || "").match(/\.(pdf)$/i) ? <FileText size={12} /> : <ImageIcon size={12} />} {t("view")}
                          </a>
                        )}
                        <span className="font-display font-bold text-warning shrink-0 w-28 text-right">
                          −{formatCurrency(Number(it.amount) || 0)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Inline "add post-sale expense" — handy when the dealer needs
                  to book a warranty repair or late detailing AFTER the car was
                  already sold without having to go back to the inventory.
                  These items still live in vehicle.expense_items, so the
                  profit math stays per-vehicle (no need to bucket them as
                  general operational expenses). */}
              {!adding ? (
                <button
                  type="button"
                  data-testid="vex-add-post-sale"
                  onClick={() => setAdding(true)}
                  className="mt-3 w-full px-4 py-3 border-2 border-dashed border-primary text-primary hover:bg-primary hover:text-white text-xs font-display font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2 transition-colors"
                >
                  <Plus size={13} /> {t("vex_add_post_sale") || "Adicionar despesa pós-venda"}
                </button>
              ) : (
                <PostSaleExpenseForm
                  vehicle={vehicle}
                  t={t}
                  onCancel={() => setAdding(false)}
                  onSaved={async () => {
                    setAdding(false);
                    await load();
                    onChanged?.();
                  }}
                />
              )}
            </div>

            {/* Footer actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-6 border-t border-border bg-surface/30">
              <p className="text-xs text-text-secondary leading-relaxed flex-1 min-w-[200px]">
                {t("revert_sale_hint")}
              </p>
              <button
                type="button"
                data-testid="revert-sale"
                onClick={() => setRevertOpen(true)}
                className="border border-warning text-warning hover:bg-warning/10 px-4 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2"
              >
                <RotateCcw size={14} /> {t("revert_to_stock")}
              </button>
            </div>
          </>
        )}
      </div>
      {revertOpen && (
        <RevertSaleDialog
          vehicleId={vehicleId}
          t={t}
          onClose={() => setRevertOpen(false)}
          onDone={() => { setRevertOpen(false); onChanged?.(); onClose(); }}
        />
      )}
    </div>
  );
}

const LOST_REASONS = ["financing_denied", "client_changed_mind", "mechanical_issue", "price_disagreement", "found_better_deal", "other"];

function RevertSaleDialog({ vehicleId, t, onClose, onDone }) {
  const [reason, setReason] = useState("client_changed_mind");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/vehicles/${vehicleId}/revert-sale`, { reason, notes });
      toast.success(t("returned_to_stock"));
      onDone();
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[60] flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={submit} className="bg-background border border-warning w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="label-eyebrow text-warning mb-1">{t("revert_to_stock")}</p>
            <h2 className="font-display font-black text-lg uppercase tracking-tight">{t("lost_sale_title")}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>
        <p className="text-xs text-text-secondary leading-relaxed border-l-2 border-warning pl-3">
          {t("lost_sale_hint")}
        </p>
        <div>
          <label className="label-eyebrow block mb-2">{t("lost_reason")}</label>
          <div className="space-y-2">
            {LOST_REASONS.map((r) => (
              <label key={r} className={`flex items-center gap-2 px-3 py-2.5 border cursor-pointer transition-colors ${reason === r ? "border-warning bg-warning/10" : "border-border hover:border-warning/60"}`}>
                <input
                  type="radio"
                  name="reason"
                  data-testid={`reason-${r}`}
                  value={r}
                  checked={reason === r}
                  onChange={() => setReason(r)}
                  className="accent-warning"
                />
                <span className="text-sm">{t(`reason_${r}`)}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("notes")}</label>
          <textarea
            data-testid="lost-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full bg-surface border border-border focus:border-warning focus:outline-none px-3 py-2 text-sm"
            placeholder={t("lost_notes_placeholder")}
          />
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="submit" data-testid="revert-confirm" disabled={saving} className="bg-warning text-black hover:opacity-90 disabled:opacity-50 px-4 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
            <RotateCcw size={14} /> {saving ? "..." : t("confirm")}
          </button>
        </div>
      </form>
    </div>
  );
}


function CloseMonthModal({ year, month, monthLabel, closing, t, onClose, onDone }) {
  const [markPaid, setMarkPaid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/financial/closings", { year, month, mark_commissions_paid: markPaid });
      toast.success(t("close_month_success"));
      // Auto-download the freshly generated PDF
      try {
        const pdf = await api.get(`/financial/closings/${r.data.id}/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(pdf.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `fechamento-${year}-${String(month).padStart(2, "0")}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch { /* user can re-download from archive */ }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4" data-testid="close-month-modal">
      <div className="bg-background border border-border w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase tracking-tight">
            {t("close_month_title")} <span className="text-primary">{monthLabel} {year}</span>
          </h2>
          <button onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        <p className="text-sm text-text-secondary">{t("close_month_intro")}</p>

        {/* Snapshot preview */}
        <div className="border border-border bg-surface divide-y divide-border">
          <div className="flex justify-between px-3 py-2 text-sm">
            <span className="text-text-secondary">{t("closing_funded_vehicles")}</span>
            <span className="font-display font-bold">{closing?.vehicles_count || 0}</span>
          </div>
          <div className="flex justify-between px-3 py-2 text-sm">
            <span className="text-text-secondary">{t("gross_profit")}</span>
            <span className="font-display font-bold text-success">{formatCurrency(closing?.gross_profit || 0)}</span>
          </div>
          <div className="flex justify-between px-3 py-2 text-sm">
            <span className="text-text-secondary">{t("operational_total")}</span>
            <span className="font-display font-bold text-warning">−{formatCurrency(closing?.operational_total || 0)}</span>
          </div>
          {Number(closing?.credits_total || 0) > 0 && (
            <div className="flex justify-between px-3 py-2 text-sm">
              <span className="text-text-secondary">{t("credits_total")}</span>
              <span className="font-display font-bold text-success">+{formatCurrency(closing?.credits_total || 0)}</span>
            </div>
          )}
          <div className="flex justify-between px-3 py-2 text-sm">
            <span className="text-text-secondary">{t("paid_commissions")}</span>
            <span className="font-display font-bold text-warning">−{formatCurrency(closing?.paid_commissions || 0)}</span>
          </div>
          {Number(closing?.receivables_total || 0) > 0 && (
            <div className="flex justify-between px-3 py-2 text-sm">
              <span className="text-text-secondary inline-flex items-center gap-1">
                {t("closing_receivables_received")}
                <span className="text-[9px] uppercase tracking-widest text-text-secondary/60">
                  ({closing.receivables_count})
                </span>
              </span>
              <span className="font-display font-bold text-success">+{formatCurrency(closing.receivables_total)}</span>
            </div>
          )}
          <div className="flex justify-between px-3 py-2 text-base bg-primary/10">
            <span className="font-display font-bold uppercase">{t("net_profit")}</span>
            <span className={`font-display font-black text-lg ${(closing?.net_profit || 0) >= 0 ? "text-success" : "text-primary"}`}>
              {formatCurrency(closing?.net_profit || 0)}
            </span>
          </div>
        </div>

        {/* Open deals carryover banner */}
        {Number(closing?.open_deals_count || 0) > 0 && (
          <div
            className="border border-warning/40 bg-warning/[0.06] p-3"
            data-testid="closing-open-deals-banner"
          >
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <p className="label-eyebrow text-warning inline-flex items-center gap-1">
                <Clock size={11} /> {t("closing_open_deals_title")}
              </p>
              <span className="text-[10px] uppercase tracking-widest font-display font-bold text-warning">
                {closing.open_deals_count} · {formatCurrency(closing.open_revenue || 0)}
              </span>
            </div>
            <p className="text-[11px] text-text-secondary leading-snug">
              {t("closing_open_deals_hint")}
            </p>
          </div>
        )}

        {/* Mark commissions paid checkbox */}
        <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="mark-commissions-paid"
            checked={markPaid}
            onChange={(e) => setMarkPaid(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="font-display font-bold">{t("mark_commissions_paid_label")}</span>
            <span className="block text-xs text-text-secondary">{t("mark_commissions_paid_hint")}</span>
          </span>
        </label>

        <div className="flex justify-end gap-3 pt-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            data-testid="confirm-close-month"
            onClick={submit}
            disabled={submitting}
            className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest text-white transition-colors inline-flex items-center gap-2"
          >
            <Lock size={13} /> {submitting ? "..." : t("close_and_export")}
          </button>
        </div>
      </div>
    </div>
  );
}


function ClosingsBarChart({ closings, t }) {
  // Sort chronologically (oldest -> newest), keep last 12
  const sorted = [...closings]
    .sort((a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month))
    .slice(-12);
  if (sorted.length === 0) return null;
  const monthShort = ["", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const max = Math.max(...sorted.map(c => Math.abs(c.net_profit || 0)), 1);
  return (
    <div className="px-4 py-5 border-b border-border" data-testid="closings-chart">
      <p className="label-eyebrow text-text-secondary mb-4">{t("monthly_evolution")}</p>
      <div className="grid grid-flow-col auto-cols-fr gap-2 items-end h-40">
        {sorted.map(c => {
          const v = c.net_profit || 0;
          const positive = v >= 0;
          const heightPct = Math.max(4, Math.round((Math.abs(v) / max) * 100));
          return (
            <div key={c.id} className="flex flex-col items-center justify-end h-full group" title={`${monthShort[c.month]}/${c.year} · ${formatCurrency(v)}`}>
              <span className={`text-[9px] font-display font-bold mb-1 ${positive ? "text-success" : "text-primary"}`}>
                {formatCurrency(v).replace(/\u00a0/g, " ")}
              </span>
              <div
                className={`w-full transition-all ${positive ? "bg-success/70 group-hover:bg-success" : "bg-primary/70 group-hover:bg-primary"}`}
                style={{ height: `${heightPct}%` }}
              />
              <span className="text-[10px] text-text-secondary uppercase tracking-wider mt-1">
                {monthShort[c.month]}/{String(c.year).slice(2)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * PostSaleExpenseForm — inline form to add an expense to a vehicle AFTER
 * the sale was already booked. The new item gets appended to the vehicle's
 * `expense_items` array and is flagged `post_sale: true` so we can spot
 * (and report on) post-delivery costs in the future.
 *
 * The expense reduces the vehicle's individual profit — NOT a general
 * dealership expense — so warranty repairs and late detailing stay tied
 * to the right car for proper margin tracking.
 */
function PostSaleExpenseForm({ vehicle, t, onCancel, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today,
    category: "post_sale",
    description: "",
    amount: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount) || 0;
    if (!form.description.trim()) {
      toast.error(t("required_description") || "Descrição obrigatória");
      return;
    }
    if (amt <= 0) {
      toast.error(t("amount_must_be_positive") || "Valor inválido");
      return;
    }
    setSaving(true);
    try {
      const existing = vehicle?.expense_items || [];
      const newItem = {
        id: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : String(Date.now()),
        date: form.date,
        category: form.category,
        description: form.description.trim(),
        amount: amt,
        post_sale: true,   // marker so future reports can filter these out
      };
      await api.put(`/vehicles/${vehicle.id}`, {
        expense_items: [...existing, newItem],
      });
      toast.success(t("saved") || "Salvo");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic") || "Erro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="post-sale-expense-form"
      className="mt-3 border border-primary p-4 bg-primary/[0.04] space-y-3"
    >
      <p className="label-eyebrow text-primary">
        {t("vex_add_post_sale") || "Adicionar despesa pós-venda"}
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1.5">{t("description") || "Descrição"}</label>
          <input
            type="text"
            data-testid="post-sale-description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder={t("post_sale_desc_placeholder") || "Ex: Troca de pastilha de freio"}
            className="w-full h-10 bg-background border border-border focus:border-primary focus:outline-none px-3 text-sm"
            autoFocus
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">{t("amount") || "Valor"} (US$)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            data-testid="post-sale-amount"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
            className="w-full h-10 bg-background border border-border focus:border-primary focus:outline-none px-3 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">{t("date") || "Data"}</label>
          <input
            type="date"
            data-testid="post-sale-date"
            value={form.date}
            onChange={(e) => set("date", e.target.value)}
            className="w-full h-10 bg-background border border-border focus:border-primary focus:outline-none px-3 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">{t("category") || "Categoria"}</label>
          <select
            data-testid="post-sale-category"
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            className="w-full h-10 bg-background border border-border focus:border-primary focus:outline-none px-3 text-sm"
          >
            <option value="post_sale">{t("cat_post_sale") || "Pós-venda"}</option>
            <option value="maintenance">{t("cat_maintenance") || "Manutenção"}</option>
            <option value="parts">{t("parts") || "Peças"}</option>
            <option value="taxes">{t("cat_taxes") || "Impostos"}</option>
            <option value="other">{t("cat_other") || "Outros"}</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="h-9 px-4 border border-border text-text-secondary hover:text-primary hover:border-primary text-[11px] font-display font-bold uppercase tracking-widest"
        >
          {t("cancel") || "Cancelar"}
        </button>
        <button
          type="submit"
          data-testid="post-sale-save"
          disabled={saving}
          className="h-9 px-5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-[11px] font-display font-bold uppercase tracking-widest inline-flex items-center gap-2"
        >
          <Check size={13} /> {saving ? "..." : (t("save") || "Salvar")}
        </button>
      </div>
    </form>
  );
}


/**
 * FinancialAuditModal — read-only integrity check for the closing.
 *
 * Consumes the /financial/audit endpoint and surfaces 4 buckets of findings:
 *   1. Zero-cost cars (purchase_price = 0 → profit inflated)
 *   2. Open commissions (unpaid → profit inflated until paid)
 *   3. In-house receivables (potential double count with sold_price)
 *   4. Suspicious expenses (manual entries that could double-count commissions)
 *
 * Each bucket shows the count, financial impact, and a list of the actual
 * offending rows so the owner can go fix them one by one.
 */
function FinancialAuditModal({ data, monthLabel, onClose }) {
  const findings = data?.findings || {};
  const reportedProfit = data?.reported_net_profit || 0;
  const projectedProfit = data?.projected_profit_if_all_commissions_paid || 0;
  const missingCost = data?.projected_max_missing_cost || 0;
  const totalIssues =
    (findings.zero_cost_cars?.count || 0) +
    (findings.open_commissions?.count || 0) +
    (findings.in_house_receivables?.count || 0) +
    (findings.suspicious_expenses?.count || 0);

  return createPortal(
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background border border-border max-w-5xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="audit-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-border sticky top-0 bg-background z-10 flex items-start justify-between gap-4">
          <div>
            <p className="label-eyebrow text-warning">Auditoria do fechamento</p>
            <h2 className="font-display font-black text-2xl mt-1">
              {monthLabel} · {totalIssues} {totalIssues === 1 ? "achado" : "achados"}
            </h2>
            <p className="text-xs text-text-secondary mt-2 max-w-xl leading-relaxed">
              Este relatório é <b>somente leitura</b> — não altera nenhum dado nem fórmula.
              Ele mostra tudo que pode estar inflando ou deflacionando o lucro reportado
              para você decidir o que corrigir.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="audit-close"
            className="text-text-secondary hover:text-primary flex-shrink-0"
            aria-label="Fechar"
          >
            <X size={22} />
          </button>
        </div>

        {/* Comparison strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border-b border-border">
          <div className="bg-background p-5">
            <p className="label-eyebrow text-text-secondary mb-2">Lucro reportado agora</p>
            <p className="font-display font-black text-2xl leading-none" data-testid="audit-reported-profit">
              {formatUSD(reportedProfit)}
            </p>
          </div>
          <div className="bg-background p-5">
            <p className="label-eyebrow text-text-secondary mb-2">Projeção · todas comissões pagas</p>
            <p
              className={`font-display font-black text-2xl leading-none ${
                projectedProfit < reportedProfit ? "text-warning" : "text-success"
              }`}
              data-testid="audit-projected-profit"
            >
              {formatUSD(projectedProfit)}
            </p>
            {projectedProfit !== reportedProfit && (
              <p className="text-[10px] text-text-secondary mt-1 font-mono">
                Δ {formatUSD(projectedProfit - reportedProfit)}
              </p>
            )}
          </div>
          <div className="bg-background p-5">
            <p className="label-eyebrow text-text-secondary mb-2">Custo invisível (máx)</p>
            <p className="font-display font-black text-2xl leading-none text-primary" data-testid="audit-missing-cost">
              {formatUSD(missingCost)}
            </p>
            <p className="text-[10px] text-text-secondary mt-1">
              Se todos os carros com custo zero fossem preenchidos
            </p>
          </div>
        </div>

        {/* Findings */}
        <div className="p-6 space-y-6">
          <AuditFinding
            title="Carros com preço de compra zero"
            eyebrow={`${findings.zero_cost_cars?.count || 0} carro(s) · lucro inflado em até ${formatUSD(findings.zero_cost_cars?.impact || 0)}`}
            help={findings.zero_cost_cars?.help}
            items={findings.zero_cost_cars?.items || []}
            columns={[
              { key: "label", label: "Veículo" },
              { key: "vin", label: "VIN", mono: true },
              { key: "sold_price", label: "Preço venda", money: true, right: true },
              { key: "salesperson_name", label: "Vendedor" },
            ]}
            testid="audit-zero-cost"
            severity="danger"
          />

          <AuditFinding
            title="Comissões em aberto"
            eyebrow={`${findings.open_commissions?.count || 0} comissão(ões) · lucro inflado em ${formatUSD(findings.open_commissions?.impact || 0)}`}
            help={findings.open_commissions?.help}
            items={findings.open_commissions?.items || []}
            columns={[
              { key: "label", label: "Veículo" },
              { key: "salesperson_name", label: "Vendedor" },
              { key: "commission_amount", label: "Comissão", money: true, right: true },
            ]}
            testid="audit-open-commissions"
            severity="warning"
          />

          <AuditFinding
            title="Recebíveis vinculados a vendas do mês"
            eyebrow={`${findings.in_house_receivables?.count || 0} recebível(is) · verifique risco de duplo cômputo`}
            help={findings.in_house_receivables?.help}
            items={findings.in_house_receivables?.items || []}
            columns={[
              { key: "vehicle_label", label: "Veículo" },
              { key: "customer_name", label: "Cliente" },
              { key: "vehicle_sold_price", label: "Preço venda", money: true, right: true },
              { key: "total_amount", label: "Total receb.", money: true, right: true },
              { key: "installment_count", label: "Parcelas", right: true },
            ]}
            testid="audit-in-house"
            severity="warning"
          />

          <AuditFinding
            title="Despesas com descrição suspeita (possível comissão)"
            eyebrow={`${findings.suspicious_expenses?.count || 0} despesa(s) · impacto ${formatUSD(findings.suspicious_expenses?.impact || 0)}`}
            help={findings.suspicious_expenses?.help}
            items={findings.suspicious_expenses?.items || []}
            columns={[
              { key: "date", label: "Data" },
              { key: "category", label: "Categoria" },
              { key: "description", label: "Descrição" },
              { key: "amount", label: "Valor", money: true, right: true },
            ]}
            testid="audit-suspicious"
            severity="warning"
          />

          {totalIssues === 0 && (
            <div className="text-center py-12 border border-dashed border-border">
              <CheckCircle2 size={40} className="mx-auto text-success mb-3" />
              <p className="font-display font-bold text-lg">Fechamento limpo</p>
              <p className="text-sm text-text-secondary mt-1">
                Nenhum problema encontrado nas 4 categorias auditadas neste mês.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AuditFinding({ title, eyebrow, help, items, columns, testid, severity }) {
  const [expanded, setExpanded] = useState(false);
  const border =
    severity === "danger" ? "border-primary/40" :
    severity === "warning" ? "border-warning/40" : "border-border";
  const iconColor = severity === "danger" ? "text-primary" : "text-warning";
  const isEmpty = items.length === 0;

  return (
    <div className={`border ${isEmpty ? "border-border" : border} p-4`} data-testid={testid}>
      <div className="flex items-start gap-3">
        {isEmpty ? (
          <CheckCircle2 size={18} className="text-success mt-0.5 flex-shrink-0" />
        ) : (
          <AlertTriangle size={18} className={`${iconColor} mt-0.5 flex-shrink-0`} />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-sm uppercase tracking-wider">{title}</p>
          <p className="text-xs text-text-secondary mt-0.5">{eyebrow}</p>
        </div>
        {!isEmpty && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] uppercase tracking-widest text-text-secondary hover:text-primary font-display font-bold"
            data-testid={`${testid}-toggle`}
          >
            {expanded ? "Recolher" : "Ver detalhes"}
          </button>
        )}
      </div>

      {expanded && !isEmpty && (
        <>
          {help && (
            <p className="text-xs text-text-secondary mt-3 pl-8 leading-relaxed italic">{help}</p>
          )}
          <div className="mt-3 pl-8 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-border">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className={`text-${c.right ? "right" : "left"} py-2 pr-3 label-eyebrow`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b border-border/40">
                    {columns.map((c) => {
                      const val = item[c.key];
                      const display = c.money
                        ? formatUSD(Number(val) || 0)
                        : String(val ?? "—");
                      return (
                        <td
                          key={c.key}
                          className={`py-2 pr-3 ${c.right ? "text-right" : ""} ${c.mono ? "font-mono" : ""}`}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
